'use client';

import { memo } from 'react';

import { useIsWorkspaceAdmin } from '@/shared/hooks/use-is-workspace-admin';

import { cn } from '@/lib/utils';

// Imported directly, not via the `charts` barrel: that barrel pulls in
// `@/shared/components`, which re-exports this file.
import { LockedOverlay } from './charts/locked-overlay';

type AdminOnlyPlaceholderProps = {
	/** Rows drawn behind the blur — roughly the real section's height. */
	rows?: number;
	className?: string;
};

/**
 * Blurs a static skeleton, never real content: admin-only data would 403 for
 * members or leak into the DOM.
 */
export const AdminOnlyPlaceholder = memo(function AdminOnlyPlaceholder({
	rows = 3,
	className,
}: AdminOnlyPlaceholderProps) {
	return (
		<LockedOverlay
			isLocked
			isAdminOnly
			className={cn('min-h-[120px]', className)}
		>
			<div
				aria-hidden
				className="overflow-hidden rounded-lg border border-border"
			>
				{Array.from({ length: rows }, (_, i) => (
					<div
						key={i}
						className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 last:border-b-0"
					>
						<div className="h-3 w-24 rounded bg-muted" />
						<div className="h-7 w-40 rounded bg-muted" />
					</div>
				))}
			</div>
		</LockedOverlay>
	);
});

/** Children are never mounted for members, so their admin-only queries never fire. */
export function AdminOnly({
	children,
	...placeholderProps
}: AdminOnlyPlaceholderProps & { children: React.ReactNode }) {
	const isAdmin = useIsWorkspaceAdmin();

	if (isAdmin === undefined) return null;
	return isAdmin ? <>{children}</> : <AdminOnlyPlaceholder {...placeholderProps} />;
}
